import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SEO from "@/components/feature/SEO";
import { webpageSchema, breadcrumbSchema, siteUrl } from "@/lib/schemas";
import { Shield, Lock, FileText, Server, Mail } from "lucide-react";

export default function PrivacyPolicyPage() {
  const baseUrl = siteUrl;
  const pageUrl = `${baseUrl}/privacy-policy`;

  const schemas = [
    webpageSchema(
      "Privacy Policy | ExpertAdvise.in",
      "ExpertAdvise.in privacy policy explains how we collect, use, and safeguard your personal information for insurance and financial advisory services.",
      pageUrl
    ),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: "Privacy Policy", url: pageUrl },
    ]),
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Privacy Policy | ExpertAdvise.in"
        description="ExpertAdvise.in privacy policy explains how we collect, use, and safeguard your personal information for insurance and financial advisory services."
        canonical={pageUrl}
        ogUrl={pageUrl}
        schemas={schemas}
      />
      <Navbar />

      <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <Breadcrumbs
              items={[
                { name: "Home", href: "/" },
                { name: "Privacy Policy" },
              ]}
              className="justify-center mb-4"
            />
            <span className="inline-block bg-primary-50 text-primary-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
              Legal
            </span>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">Privacy Policy</h1>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed">
              ExpertAdvise.in is committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your data.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 space-y-8">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-primary-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">1. Information We Collect</h2>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed pl-[52px]">
                We collect personal information that you voluntarily provide when using our services, including name, email address, phone number, city, age, and insurance requirements. We also collect information about your interactions with our website for analytics and service improvement purposes.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-primary-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">2. How We Use Your Information</h2>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed pl-[52px]">
                We use your information to provide insurance advisory services, process your consultation requests, communicate with you about relevant products, improve our services, and comply with legal obligations. We do not sell your personal information to third parties.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-primary-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">3. Data Security</h2>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed pl-[52px]">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. All data is encrypted in transit and at rest using industry-standard protocols.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                  <Server className="w-5 h-5 text-primary-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">4. Data Sharing</h2>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed pl-[52px]">
                We may share your information with insurance companies, TPAs, and regulatory authorities as necessary to provide our services. All third-party partners are bound by confidentiality obligations and data protection standards. We never sell your data to marketing companies.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-primary-600" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">5. Your Rights</h2>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed pl-[52px]">
                You have the right to access, correct, delete, or restrict processing of your personal information. To exercise these rights, please contact us at info@expertadvise.in or call +91 99993 65655.
              </p>
            </div>

            <div className="pt-6 border-t border-slate-100">
              <p className="text-sm text-slate-500 text-center">
                Last updated: January 2024. For questions about this Privacy Policy, please{" "}
                <Link to="/contact" className="text-primary-600 hover:underline">contact us</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}